import React, { useEffect, useState } from 'react';
import { getInvite, acceptInvite, authLogin, authRegister } from '../api';

interface InviteInfo {
  id: string;
  organization_name: string;
  email: string;
  role_name: string;
  invited_by_name: string;
  expires_at: string;
  accepted_at: string | null;
  token: string;
}

interface Props {
  token: string;
  onAccepted: () => void;
}

type Step = 'loading' | 'info' | 'accepting' | 'done' | 'error';

function OrgAvatar({ name }: { name: string }) {
  const initials = name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
  return (
    <div className="invite-org-avatar">
      {initials}
    </div>
  );
}

export default function AcceptInvite({ token, onAccepted }: Props) {
  const [step, setStep] = useState<Step>('loading');
  const [invite, setInvite] = useState<InviteInfo | null>(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [authMode, setAuthMode] = useState<'login' | 'register'>('login');

  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await getInvite(token);
        const data: InviteInfo = res.data;

        if (data.accepted_at) {
          setErrorMsg('This invitation has already been accepted.');
          setStep('error');
          return;
        }
        const expires = new Date(data.expires_at);
        if (expires < new Date()) {
          setErrorMsg('This invitation has expired. Please ask for a new one.');
          setStep('error');
          return;
        }

        setInvite(data);
        setEmail(data.email);

        const savedToken = localStorage.getItem('access_token');
        if (savedToken) {
          setStep('accepting');
          await doAccept();
        } else {
          setStep('info');
        }
      } catch {
        setErrorMsg('Invite not found or has expired.');
        setStep('error');
      }
    })();
  }, [token]);

  const doAccept = async () => {
    setStep('accepting');
    try {
      await acceptInvite(token);
      setStep('done');
      setTimeout(onAccepted, 1500);
    } catch (e: any) {
      setErrorMsg(e.response?.data?.detail || 'Failed to accept invite. Please try again.');
      setStep('error');
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setErrorMsg('');
    try {
      const res = await authLogin({ email, password });
      localStorage.setItem('access_token', res.data.access_token);
      localStorage.setItem('refresh_token', res.data.refresh_token);
      await doAccept();
    } catch (e: any) {
      setErrorMsg(e.response?.data?.detail || 'Invalid email or password');
      setSubmitting(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setErrorMsg('');
    try {
      const res = await authRegister({ email, full_name: fullName, password, invite_token: token });
      localStorage.setItem('access_token', res.data.access_token);
      localStorage.setItem('refresh_token', res.data.refresh_token);
      setStep('done');
      setTimeout(onAccepted, 1500);
    } catch (e: any) {
      setErrorMsg(e.response?.data?.detail || 'Registration failed');
      setSubmitting(false);
    }
  };

  // ── Full-screen states ───────────────────────────────────────────────────────

  if (step === 'loading') {
    return (
      <div className="invite-loading-screen">
        <div className="spinner" style={{ width: 32, height: 32 }} />
        <span>Loading invitation…</span>
      </div>
    );
  }

  if (step === 'accepting') {
    return (
      <div className="invite-loading-screen">
        <div className="spinner" style={{ width: 32, height: 32 }} />
        <span>Joining <strong>{invite?.organization_name}</strong>…</span>
      </div>
    );
  }

  if (step === 'done') {
    return (
      <div className="invite-loading-screen">
        <div className="invite-done-icon">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
            <polyline points="22 4 12 14.01 9 11.01" />
          </svg>
        </div>
        <h2 style={{ margin: '12px 0 6px', color: 'var(--text-heading)' }}>You're in!</h2>
        <p style={{ color: 'var(--text-muted)', margin: 0 }}>
          Welcome to <strong>{invite?.organization_name}</strong>. Redirecting…
        </p>
      </div>
    );
  }

  if (step === 'error') {
    return (
      <div className="invite-loading-screen">
        <div className="invite-error-icon">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="15" y1="9" x2="9" y2="15" />
            <line x1="9" y1="9" x2="15" y2="15" />
          </svg>
        </div>
        <h2 style={{ margin: '12px 0 6px', color: 'var(--text-heading)' }}>Invite Invalid</h2>
        <p style={{ color: 'var(--text-muted)', margin: '0 0 24px', maxWidth: 340, textAlign: 'center' }}>{errorMsg}</p>
        <a href="/" className="btn btn-primary" style={{ textDecoration: 'none' }}>Go to Sign In</a>
      </div>
    );
  }

  // ── Main invite flow (step === 'info') ──────────────────────────────────────

  return (
    <div className="auth-split-page">
      {/* ── Left: Org invitation context ──────────────────────────── */}
      <div className="auth-split-left invite-left">
        <div className="auth-split-brand">
          {/* App logo */}
          <div className="auth-split-logo" style={{ marginBottom: 48 }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
              <polyline points="3.27 6.96 12 12.01 20.73 6.96" stroke="#fff" />
              <line x1="12" y1="22.08" x2="12" y2="12" stroke="#fff" />
            </svg>
            <span>ASIN Tracker</span>
          </div>

          {/* Invite card */}
          <div className="invite-context-card">
            <p className="invite-context-from">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
              <strong>{invite?.invited_by_name}</strong> invited you to join
            </p>
            {invite && <OrgAvatar name={invite.organization_name} />}
            <h2 className="invite-context-org">{invite?.organization_name}</h2>
            <div className="invite-context-role">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
              Your role: <strong>{invite?.role_name}</strong>
            </div>
            <div className="invite-context-expiry">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              Expires {invite ? new Date(invite.expires_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : ''}
            </div>
          </div>
        </div>
        <div className="auth-split-decoration">
          <div className="auth-deco-circle auth-deco-c1" />
          <div className="auth-deco-circle auth-deco-c2" />
        </div>
      </div>

      {/* ── Right: Auth forms ──────────────────────────────────────── */}
      <div className="auth-split-right">
        <div className="auth-form-wrap">
          <div className="auth-form-header">
            <h1>Accept your invitation</h1>
            <p>
              {authMode === 'login'
                ? 'Sign in to your existing account to join.'
                : 'Create a new account to get started.'}
            </p>
          </div>

          {/* Tab toggle */}
          <div className="invite-auth-tabs">
            <button
              className={`invite-auth-tab ${authMode === 'login' ? 'active' : ''}`}
              onClick={() => { setAuthMode('login'); setErrorMsg(''); }}
            >
              I have an account
            </button>
            <button
              className={`invite-auth-tab ${authMode === 'register' ? 'active' : ''}`}
              onClick={() => { setAuthMode('register'); setErrorMsg(''); }}
            >
              Create account
            </button>
          </div>

          {errorMsg && <div className="alert alert-error" style={{ marginBottom: 16 }}>{errorMsg}</div>}

          {authMode === 'login' ? (
            <form onSubmit={handleLogin} className="auth-form">
              <div className="form-group">
                <label>Email address</label>
                <div className="auth-input-wrap">
                  <svg className="auth-input-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                  </svg>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" autoComplete="email" required autoFocus />
                </div>
              </div>
              <div className="form-group">
                <label>Password</label>
                <div className="auth-input-wrap">
                  <svg className="auth-input-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    autoComplete="current-password"
                    required
                  />
                  <button type="button" className="auth-eye-btn" onClick={() => setShowPassword(!showPassword)}>
                    {showPassword
                      ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" /></svg>
                      : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                    }
                  </button>
                </div>
              </div>
              <button type="submit" className="btn btn-primary auth-submit" disabled={submitting}>
                {submitting ? 'Signing in…' : 'Sign In & Accept Invite'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleRegister} className="auth-form">
              <div className="form-group">
                <label>Full Name</label>
                <div className="auth-input-wrap">
                  <svg className="auth-input-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                  <input type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="John Smith" autoComplete="name" required autoFocus />
                </div>
              </div>
              <div className="form-group">
                <label>Email address</label>
                <div className="auth-input-wrap">
                  <svg className="auth-input-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                  </svg>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" autoComplete="email" required />
                </div>
              </div>
              <div className="form-group">
                <label>Password</label>
                <div className="auth-input-wrap">
                  <svg className="auth-input-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                  </svg>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Min. 6 characters"
                    autoComplete="new-password"
                    required
                    minLength={6}
                  />
                  <button type="button" className="auth-eye-btn" onClick={() => setShowPassword(!showPassword)}>
                    {showPassword
                      ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" /></svg>
                      : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                    }
                  </button>
                </div>
              </div>
              <button type="submit" className="btn btn-primary auth-submit" disabled={submitting}>
                {submitting ? 'Creating account…' : 'Create Account & Accept Invite'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
